import React from 'react';
import { GOOGLE_ANALYTICS, MIX_PANEL, PERMISSION_LEVELS } from '@src/const';

import Text from 'relements/build/esm/ui/text';
import Box from 'relements/build/esm/ui/box';

import { dispatch } from '@src/dispatcher';
import * as Api from '@src/api';
import * as CodeApi from '@src/containers/CodeNode/store/api';
import * as DynamicApi from '@src/containers/DynamicNode/store/api';
import { updateABTestSplitDetails } from '@src/api/DomainAPI';
import * as Utils from 'utils';
import {
  ActionTypes,
  AlertTypes,
  GRAPH_NODE_DISPLAY_TEXT,
  END_OF_QUERY,
} from '@src/constants/MogamboConstants';

import { getDomainData } from '@src/actions/DomainActions';

// Store
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';

import {
  userHasPermission,
  permissionErrorAlert,
  createAlert,
  sendGoogleAnalyticsEvents,
  startLoading,
  stopLoading,
} from '../_utils';

import { fileDownload } from './utils';
import { updateAissNode } from '../../containers/AissNode/store/api';

export {
  openComponentTooltip,
  closeComponentTooltip,
} from './component_tooltip';
export {
  getBotResponses,
  updateBotResponses,
  saveBotResponses,
  updateEntities,
} from './entities';
export { getSynonyms, getMeanings, updateSynonyms } from './synonyms';
export {
  createAlert,
  destroyAlert,
  userHasPermission,
  permissionErrorAlert,
  startLoading,
  stopLoading,
} from '../_utils';

export function logout() {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    title: 'Logout',
    message: 'Are you sure you want to logout?',
    okTitle: 'Logout',
    cancelTitle: 'Cancel',
    callback: () => {
      dispatch('LOGOUT', {
        data: null,
      });
      Api.logout();
    },
  });
}

export function locationChanged(location) {
  if (location.action == 'POP') {
    dispatch('LOCATION_CHANGED', {
      data: location,
    });
  }
}

export function getAllBots(callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert('view all bots');
  }
  dispatch('GET_ALL_BOTS', {
    message: 'Fetching all Bots',
  });
  Api.getAllBots(callback);
}

export function getMetaInfo(key, value, cb) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();
  Api.getMetaInfo(key, value, cb);
}

export function getAllSubStories(storyId) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();

  dispatch('GET_ALL_SUB_STORIES', {
    data: null,
    message: 'Fetching Skills',
  });
  Api.getAllSubStories(storyId);
}

export function getSubstoryGraph(storyId, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();
  Api.getSubstoryGraph(storyId, callback);
}

export function getGeneralResponses(botId, callback) {
  /*
    Action to get Bot Specific General Responses
    */
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();

  dispatch(ActionTypes.GET_GENERAL_RESPONSES, {
    message: 'Getting General Responses',
  });

  Api.getGeneralResponses(botId, callback);
}

export function getStepPriorities(botId) {
  /*
    Action to get Bot Specific Node Priority
    */
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();

  dispatch(ActionTypes.GET_NODE_PRIORITY, {
    message: 'Getting Step Priority',
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.NODE_PRIORITY,
    GOOGLE_ANALYTICS.EVENTS.NODE_PRIORITY_VIEW,
    'view_priority_nodes',
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.getStepPriorities(botId);
}

export function getAllUserResponses(id, currentLanguage, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();
  Api.getAllUserResponses(id, currentLanguage, callback);
}

export function getDisambiguationTexts(domainId, sentenceId, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert('view Disambiguation Texts.');
  Api.getDisambiguationTexts(domainId, sentenceId, callback);
}

export function confirmTransferBot(botData, headers) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_TRANSFER))
    return permissionErrorAlert();

  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: transferBot,
    title: 'Publish',
    message: 'Do you really want to Publish?',
    okTitle: 'Publish',
    cancelTitle: 'Cancel',
    data: {
      domain_id: botData.title,
      headers,
      bot_id: botData.bot_id,
    },
  });
}

export function confirmDeployBot(domainName, callback = () => null) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback,
    title: 'Train Bot',
    message: (
      <>
        <span> Do you want to Train the Bot?</span>
        <Box
          style={{
            marginTop: 14,
            background: 'rgb(247, 97, 124)',
            borderRadius: 4,
          }}
        >
          <Text
            style={{
              color: '#ffffff',
              lineHeight: 1.5,
            }}
          >
            WARNING: 3.6, 3.7 Python runtime used for code node is now
            deprecated and moved to 3.9. If you have updated your code node,
            please test it before publishing to production.
          </Text>
        </Box>
      </>
    ),
    okTitle: 'Train',
    cancelTitle: 'Cancel',
  });
}

export function transferBot(data, domain) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_TRANSFER))
    return permissionErrorAlert();

  dispatch('TRANSFER_BOT', {
    id: { domain_id: data.domain_id },
  });

  Api.transferBot(data.domain_id, data.headers, async () => {
    // GA event on success
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.TRANSFER,
      GOOGLE_ANALYTICS.EVENTS.BOT_TRANSFER,
      `bot-${data.bot_id}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );

    const botData = await getDomainData(domain.id);

    if (BotStore.getSelfServeLayout()) {
      const topbarEvents = BotStore.getTopbarEvents();
      topbarEvents.toggleProdLock(false);
    }

    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.BOT.CONFIRM_BOT_TRANSFER,
      payload: {
        'Time of Event': new Date().toString(),
        'Bot Name': botData.domain_name,
        'Bot Partner': botData.client,
        'Bot Business': botData.test_business,
        'Bot Status': botData.active ? 'Active' : 'Inactive',
        'Bot Languages': botData.supported_languages,
        'Bot Languages Count': botData.supported_languages.length,
      },
    });
  });
}

export async function getBotSummary(id) {
  const response = await Api.getBotSummary(id);

  if (response.success) {
    return response.body;
  }

  console.error('Error Fetching Bot Data.');
}

export function deployBot(domainName, additionalHeaders, domainId) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  dispatch(ActionTypes.DEPLOY_BOT, {
    id: domainName,
  });
  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.TRAIN,
    GOOGLE_ANALYTICS.EVENTS.BOT_TRAINED,
    `domain-${domainName}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.deployBot(domainName, additionalHeaders, domainId);
}

export function deployWebguide(
  domainName,
  additionalHeaders,
  domainId,
  data = {},
  callback = () => {}
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  Api.deployBot(domainName, additionalHeaders, domainId, data, callback);
}

export function deployAIAgents(
  domainName,
  additionalHeaders,
  domainId,
  data = {},
  callback = () => {}
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  Api.deployBot(domainName, additionalHeaders, domainId, data, callback);
}

export function uploadBot(data) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  dispatch('UPLOAD_BOT', {
    data,
  });
  Api.uploadBot(data);
}

export function startBotAugmentation(domainName, additionalHeaders) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.SMART_VARIANTS,
    GOOGLE_ANALYTICS.EVENTS.SMART_VARIANTS_TRIGGERED,
    `domain-${domainName}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.startBotAugmentation(domainName, additionalHeaders);
}

export function uploadNewBot(data) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT))
    return permissionErrorAlert();

  dispatch('UPLOAD_NEW_BOT', {});
  Api.uploadNewBot(data);
}

export function confirmExtractBot(domainId) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();

  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: extractBot,
    title: 'Extract',
    message: 'Do you really want to Extract?',
    okTitle: 'Extract',
    cancelTitle: 'Cancel',
    data: {
      domain_id: domainId,
    },
  });
}

export function extractBot(data) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW))
    return permissionErrorAlert();

  dispatch('EXTRACT_BOT', {
    data,
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.EXTRACT,
    GOOGLE_ANALYTICS.EVENTS.BOT_EXTRACTED,
    `domain-${data.domain_id}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );
  Api.getBotExtract(data.domain_id);
}

export function openPopup(id) {
  dispatch('OPEN_POPUP', {
    id,
    status: true,
  });
}

export function closePopup(id) {
  dispatch('CLOSE_POPUP', {
    id,
    status: false,
  });
}

export function openComponentModal(
  children,
  title,
  subtitle,
  iconType,
  hasSaveButton,
  onIconClick,
  onSave,
  className,
  isV2,
  headerIcon,
  headerSubIcon,
  onModalClose
) {
  dispatch(ActionTypes.OPEN_COMPONENT_MODAL, {
    status: true,
    children,
    title,
    subtitle,
    iconType,
    hasSaveButton,
    onIconClick,
    onSave,
    className,
    isV2,
    headerIcon,
    headerSubIcon,
    onModalClose,
  });
}

export function closeComponentModal() {
  dispatch(ActionTypes.CLOSE_COMPONENT_MODAL, {
    status: false,
    isV2: false,
    headerIcon: null,
    headerSubIcon: null,
  });
}

export function updateComponentModal(params) {
  dispatch(ActionTypes.UPDATE_COMPONENT_MODAL, {
    params,
  });
}

export function loadingComponentModal(
  title,
  subtitle,
  iconType,
  hasSaveButton
) {
  dispatch(ActionTypes.LOADING_COMPONENT_MODAL, {
    title,
    subtitle,
    iconType,
    hasSaveButton,
  });
}

/*
 *
 *
 * Actions to update states of views when
 * there is a change in an input field's state
 * These update the store, but (usually)
 * do not emit changes or make POST api calls
 *
 *
 */
export function updateGeneralResponses(data) {
  dispatch('UPDATE_GENERAL_RESPONSES', {
    data,
  });
}

export function updateUserResponses(userResponses, editedData) {
  dispatch(ActionTypes.UPDATE_USER_RESPONSES, {
    editedData,
    userResponses,
  });
}

export function updateNegativeUserResponses(userResponses) {
  dispatch(ActionTypes.UPDATE_NEGATIVE_USER_RESPONSES, {
    userResponses,
  });
}

export function updateNodeDelayMessage(timeout, message) {
  dispatch(ActionTypes.UPDATE_NODE_DELAY_MESSAGE, {
    timeout,
    message,
  });
}

export function updateStepPriorities(data, args) {
  dispatch('UPDATE_STEP_PRIORITIES', {
    data,
    args,
  });
}

/* Get updates from store */
export function getUpdates() {
  dispatch('GET_UPDATES', {});
}

/* Reset all store data */
export function resetData() {
  dispatch('RESET_DATA', {});
}

export function createNewBot(botName, client, testBusiness) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('create new bot');
  }

  const data = Utils.convertBotData(botName, client, testBusiness);
  startLoading('Creating a new Bot');
  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.BOT,
    GOOGLE_ANALYTICS.EVENTS.BOT_CREATE,
    `domain-${botName}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );
  Api.createNewBot(data);
}

export function createNewStory(storyName, domainId, domainTitle, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('create new story');
  }

  const data = Utils.convertStoryData(storyName, domainId, domainTitle);

  dispatch('CREATE_NEW_STORY', {
    data,
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.STORY,
    GOOGLE_ANALYTICS.EVENTS.STORY_CREATE,
    `domain-${domainId}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.createNewStory(data, callback);
}

export function updateSubstory(
  allSubStories,
  storyId,
  domainId,
  domainTitle,
  additionalHeaders,
  callback,
  isEdit = false
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('create substories');
  }

  const data = Utils.convertSubstoryList(
    allSubStories,
    storyId,
    domainId,
    domainTitle
  );

  dispatch(ActionTypes.UPDATE_SUB_STORIES, {
    message: 'Sending Substories',
    allSubStories,
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.STORY,
    GOOGLE_ANALYTICS.EVENTS.SUBSTORY_CREATE,
    `story-${storyId}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.createNewSubstory(
    data,
    storyId,
    domainId,
    additionalHeaders,
    callback,
    isEdit
  );
}

export function createNewStep(
  id,
  stepName,
  stepType,
  stepAttr,
  stepStatus,
  systemName,
  modelName,
  taskId,
  subStoryId,
  domainName,
  storyId,
  domainId,
  x,
  y,
  callback,
  isGenAi,
  genAiData
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    callback(true);
    return permissionErrorAlert('update substories');
  }

  const updatedStepType = { ...stepType };
  delete updatedStepType[END_OF_QUERY.KEY];

  const data = Utils.convertStepData(
    id,
    stepName,
    updatedStepType,
    stepAttr,
    stepStatus,
    systemName,
    modelName,
    taskId,
    subStoryId,
    domainName,
    x,
    y
  );

  data[END_OF_QUERY.KEY] = stepType[END_OF_QUERY.KEY] || false;
  if (isGenAi) {
    const { userIntent, userSays, botResponse, options } = genAiData;
    const { responseType, smiley, tone, rephraseBotResponse } = options;
    data.user_intent = userIntent;
    data.bot_response = botResponse;
    data.user_response = userSays;
    data.options = {
      rephrase_bot_response: rephraseBotResponse,
      response_type: responseType,
      smiley_addition: smiley,
      tone,
    };
    data.genai = true;
  }

  // dispatch('START_LOADING', {
  //     message: 'Sending Step Data',
  // });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.NODE,
    GOOGLE_ANALYTICS.EVENTS.NODE_EDIT,
    `node-${stepName}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  Api.createNewStep({ stepInfo: data }, subStoryId, storyId, callback, isGenAi);
}

export function sendUpdatedStepPriorities(id, stepPriorities) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('update step pPriorities');
  }

  dispatch('SEND_STEP_PRIORITIES', {
    message: 'Sending Step Priorities',
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.NODE_PRIORITY,
    GOOGLE_ANALYTICS.EVENTS.NODE_PRIORITY_EDIT,
    `node-${id}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  const converted = Utils.convertStepPriorities(id, stepPriorities);
  Api.sendStepPriorities(converted);
}

export function sendUpdatedGeneralResponses(
  generalResponses,
  editedGeneralResponses,
  domainId
) {
  if (
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT) &&
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_SAYS_EDIT)
  ) {
    return permissionErrorAlert('update general responses');
  }

  dispatch('SEND_GENERAL_RESPONSES', {
    message: 'Sending General Responses',
  });

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.GENERAL_RESPONSES,
    GOOGLE_ANALYTICS.EVENTS.GENERAL_RESPONSES_EDIT,
    'edit_general_responses',
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  const converted = Utils.convertGeneralResponses(
    generalResponses,
    editedGeneralResponses
  );
  converted.id = domainId;
  Api.sendGeneralResponses(converted);
}

export async function sendUpdatedUserResponses(
  allUserResponses,
  editedUserResponses,
  stepId,
  stepName,
  domainTitle,
  editedClusters,
  editedEntities,
  currentLanguage,
  callback
) {
  // TODO : to validate if the edited user responses contain any special
  //        characters which should be skipped Like Apostrophe/full stop etc.
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    permissionErrorAlert('update user says');
    if (callback) callback();
    return;
  }

  if (
    !Object.keys(editedUserResponses).length &&
    !Object.keys(editedClusters).length &&
    !Object.keys(editedEntities).length
  ) {
    createAlert('No changes have been made in the User Responses.', 1500);
    if (callback) callback();
    return;
  }

  if (Object.keys(editedClusters).length) {
    dispatch('SEND_SYNONYMS', {
      message: 'Sending Synonyms',
    });
    const convertedSynonyms = Utils.convertClusters(
      domainTitle,
      editedClusters
    );
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.USER_SAYS,
      GOOGLE_ANALYTICS.EVENTS.SYNONYMS_EDIT,
      `node-${stepId}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
    await Api.setMeanings(convertedSynonyms);
  }

  if (Object.keys(editedEntities).length) {
    dispatch('SEND_ENTITIES', {
      message: 'Sending Entities',
    });
    const convertedEntities = Utils.convertEntities(stepName, editedEntities);
    await Api.setUserSaysEntities(convertedEntities);
  }

  if (Object.keys(editedUserResponses).length) {
    const converted = Utils.convertUserResponses(
      stepId,
      editedUserResponses,
      allUserResponses,
      currentLanguage
    );
    dispatch('SEND_USER_RESPONSES', {
      message: 'Sending User Responses',
    });

    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.USER_SAYS,
      GOOGLE_ANALYTICS.EVENTS.USER_SAYS_EDIT,
      `node-${stepId}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );

    await Api.sendUserResponses(converted, stepId, () => null, domainTitle);
  }

  if (callback) callback();
}

export const saveDisambiguationTexts = (botId, sentenceId, data, callback) => {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    permissionErrorAlert('update disambiguation texts');
    return;
  }
  Api.saveDisambiguationTexts(botId, sentenceId, data, callback);
};

export const updateCurrentStepPosition = (position) => {
  dispatch(ActionTypes.UPDATE_STEP_POSITION, {
    position,
  });
};

export async function saveNodePositions(nodeId, nodeType, posX, posY) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('update step positions');
  }

  if (nodeType === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
    await Api.updateNodeData(nodeId, {
      x: posX,
      y: posY,
    });
  } else if (nodeType === GRAPH_NODE_DISPLAY_TEXT.CODE) {
    await CodeApi.saveCodeNodeData(nodeId, {
      pos_x: posX,
      pos_y: posY,
    });
  } else if (nodeType === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
    await DynamicApi.saveDynamicNodeData(nodeId, {
      pos_x: posX,
      pos_y: posY,
    });
  } else if (nodeType === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) {
    const { domain } = CommonStore.getMetaTags();
    await updateABTestSplitDetails(domain.id, nodeId, {
      pos_x: posX,
      pos_y: posY,
    });
  } else if (nodeType === GRAPH_NODE_DISPLAY_TEXT.AISS) {
    await updateAissNode({ id: nodeId, pos_x: posX, pos_y: posY });
  }

  updateCurrentStepPosition({ id: nodeId, x: posX, y: posY });
}

export function showCloseConfirmationDialog(onOk) {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: onOk,
    title: 'Exit ?',
    message: 'Are you sure you want to exit without saving all the changes?',
    okTitle: 'Exit',
    cancelTitle: 'Cancel',
  });
}

export function showConfirmationDialog({
  okTitle = 'Yes',
  cancelTitle = 'Cancel',
  title = 'Are you sure ?',
  message = 'Please confirm',
  onOk = () => null,
  onCancel = () => null,
  okActionType = '',
  cancelActionType = '',
}) {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: onOk,
    okTitle,
    cancelTitle,
    title,
    message,
    onCancel,
    okActionType,
    cancelActionType,
  });
}

export function closeConfirmationDialog() {
  dispatch(ActionTypes.CLOSE_CONFIRMATION_DIALOG);
}

export function showBotEditor(data) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('edit bot');
  }

  dispatch('OPEN_EDIT_BOT_DIALOG', {
    data,
  });
}

export function redirectToStepEditor(botId, storyId, subStoryId, stepId) {
  window.appHistory.push(
    `bot/${botId}/${storyId}/${subStoryId}/${stepId}/edit-step`
  );
}

export function setCurrentStep(nodeId) {
  // rename showSettings to showSummary only
  Api.getNodeSummary(nodeId);
}

export function setCurrentSubStory(subStoryId) {
  dispatch('UPDATE_CURRENT_SUBSTORY', {
    data: subStoryId,
  });
}

export function addNewNode(x = 0, y = 0, meta, callback) {
  dispatch('ADD_NEW_NODE', {
    data: {
      x,
      y,
      meta,
      callback,
    },
  });
}

export function addABNode(abNodeInfo) {
  dispatch(ActionTypes.ADD_AB_NODE, {
    data: {
      abNodeInfo,
    },
  });
}

export async function getGeneralNodes(
  botId,
  storyId,
  currentLanguage,
  callback
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert('view general nodes');
  }

  let gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_BOT;
  let gaEventLabel = GOOGLE_ANALYTICS.LABEL.BOT_VIEW;

  if (storyId) {
    gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_STORY;
    gaEventLabel = GOOGLE_ANALYTICS.LABEL.STORY_VIEW;
  }

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.GENERAL_NODES,
    gaEventAction,
    gaEventLabel,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  await Api.getGeneralNodes(botId, storyId, currentLanguage);
  if (callback) callback();
}

export function clearGeneralNodes() {
  dispatch(ActionTypes.SET_GENERAL_NODES, {
    data: {},
  });
}

export function saveGeneralNodes(
  data,
  botId,
  storyId,
  currentLanguage,
  callback,
  domainTitle = ''
) {
  if (
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT) &&
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_SAYS_EDIT)
  ) {
    return permissionErrorAlert('update general nodes');
  }
  stopLoading();
  startLoading(`Saving your changes (Remaining: ${data.pending.length})`);

  Api.saveGeneralNodesApi(
    data,
    botId,
    storyId,
    currentLanguage,
    callback,
    domainTitle
  );
}

export function downloadFAQNodes(botId, domainTitle, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert('view FAQs');
  }

  Api.downloadFAQNodes(botId).then(({ success, response }) => {
    if (callback) callback();
    if (!success) {
      createAlert(
        response.error || 'Error occurred while downloading file.',
        null,
        AlertTypes.NEGATIVE
      );
      return;
    }
    fileDownload(response, `${domainTitle.toUpperCase()}_FAQSteps.tsv`);
  });
}

export function uploadFAQNodes(botId, file, callback) {
  if (
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT) &&
    !userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_SAYS_EDIT)
  ) {
    return permissionErrorAlert('update FAQs');
  }

  Api.uploadFAQNodes(botId, file).then(({ success, response }) => {
    if (!success) {
      createAlert(
        response.error || 'Error occurred while uploading file.',
        null,
        AlertTypes.NEGATIVE
      );
      return;
    }
    if (callback) callback();
  });
}

export function getBulkActionsStatus(botId) {
  return Api.getBulkActionsStatus(botId);
}

export function showDeleteEntityConfirmationDialog(onOk) {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: onOk,
    okTitle: 'Remove',
    cancelTitle: 'Cancel',
    title: 'Remove Entity ?',
    message: 'Are you sure you want to remove this entity from Bot Responses ?',
    okActionType: 'danger',
  });
}

export function showDeleteVariantConfirmationDialog(onOk) {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: onOk,
    okTitle: 'Delete',
    cancelTitle: 'Cancel',
    title: 'Delete Message!',
    message: 'Are your sure you want to delete this message ?',
  });
}

export function showDeleteReplyConfirmationDialog(onOk) {
  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback: onOk,
    okTitle: 'Delete',
    cancelTitle: 'Cancel',
    title: 'Delete Message ?',
    message: 'Are your sure you want to delete this message ?',
  });
}

export function alertEnded() {
  dispatch('DESTROY_ALERT');
}

export function updateCurrentBotSaysLanguage(languageCode, hybridLanguage) {
  dispatch(ActionTypes.SET_CURRENT_BOTSAYS_LANGUAGE, {
    languageCode,
    hybridLanguage,
  });
}

export function resetBotResponsesUpdateData() {
  dispatch(ActionTypes.RESET_BOT_RESPONSE_UPDATE_DATA);
}

export function setIsSuperman(isSuperman) {
  dispatch(ActionTypes.SET_IS_SUPERMAN, {
    isSuperman,
  });
}

export function setCurrentChannelId(channelId) {
  dispatch(ActionTypes.SET_CURRENT_CHANNEL_ID, {
    channelId,
  });
}

export function updateBotResponsesByChannel(data, callback) {
  dispatch(ActionTypes.UPDATE_BOT_RESPONSES_BY_CHANNEL, {
    data,
    callback,
  });
}

export function updateGlobalConstantsDrawerFlag(flag) {
  dispatch(ActionTypes.SET_GLOBAL_CONSTANTS_DRAWER_ACTIVE, {
    flag,
  });
}

export function updateABTestingDrawerFlag(flag) {
  dispatch(ActionTypes.SET_AB_TESTING_DRAWER_ACTIVE, {
    flag,
  });
}

export function updateABTestingRhsState(rhsState) {
  dispatch(ActionTypes.SET_AB_TESTING_RHS_STATE, {
    rhsState,
  });
}
export function updateStepSelectedForABTesting(step) {
  dispatch(ActionTypes.SET_STEP_SELECTED_FOR_AB_TESTING, {
    step,
  });
}

export function setRHSActive(flag) {
  dispatch(ActionTypes.SET_RHS_ACTIVE, {
    flag,
  });
}

export function setABTestInfo(info) {
  dispatch(ActionTypes.SET_AB_TEST_INFO, { info });
}

export function setABTestModifiedFlag(flag) {
  dispatch(ActionTypes.SET_AB_TEST_MODIFIED_FLAG, { flag });
}

export function setConstantDrawerRef(ref) {
  dispatch(ActionTypes.SET_GLOBAL_CONSTANT_DRAWER_REF, {
    ref,
  });
}

export function setGenAiModalActive(flag) {
  dispatch(ActionTypes.SET_GEN_AI_MODAL_ACTIVE, {
    flag,
  });
}
